import { useCallback, useRef, useState } from "react";
import { useSelector } from "react-redux";

import { DoubleChevLeftIcon } from "@/assets/icons";
import { Divider } from "@/components/ui/Divider";
import Tooltip from "@/components/ui/tooltip";
import {
  EModuleName,
  EModulePermission,
  useGetUserPermission
} from "@/hooks/permission/useGetUserPermission";
import { RootState } from "@/store";

import { Button } from "equator-design-system";
import { twMerge } from "tailwind-merge";

import type { SidebarFooterProps } from "../types";
import ChangePasswordPanel from "./ChangePasswordPanel";
import MyProfilePanel from "./MyProfilePanel";
import { SettingMenuWrapper } from "./SettingMenu";
import { UserMenuWrapper } from "./UserMenu";

const SidebarFooter = ({
  isExpanded,
  toggleSidebar,
  activePopover,
  setActivePopover
}: SidebarFooterProps) => {
  const settingRef = useRef<HTMLButtonElement>(null);
  const userRef = useRef<HTMLButtonElement>(null);
  const { sidebarMenu } = useSelector((state: RootState) => state.ui);

  const isPopoverTriggerOrigin = useCallback(
    (e: Event) => {
      return !!(
        settingRef.current &&
        !settingRef.current.contains(e.target as Node) &&
        userRef.current &&
        !userRef.current.contains(e.target as Node)
      );
    },
    [settingRef, userRef]
  );
  const [showMyProfilePanel, setShowMyProfilePanel] = useState(false);
  const [showChangePasswordPanel, setShowChangePasswordPanel] = useState(false);
  const userPermissions = useGetUserPermission();
  const isUserManagementGranted =
    userPermissions[EModuleName.USER_MANAGEMENT][EModulePermission.VIEW];
  const isUserGroupManagementGranted =
    userPermissions[EModuleName.USER_GROUP_MANAGEMENT][EModulePermission.VIEW];

  return (
    <div className="flex flex-col justify-center items-center gap-3">
      {activePopover !== "" && (
        <div
          className={twMerge(
            "fixed inset-0 bg-black/40 z-[1003]",
            sidebarMenu ? "left-[264px]" : "left-[60px]"
          )}
        />
      )}

      {/* Render the setting menu if user management or group management permissions are granted */}
      {(isUserManagementGranted || isUserGroupManagementGranted) && (
        <SettingMenuWrapper
          activePopover={activePopover}
          isPopoverTriggerOrigin={isPopoverTriggerOrigin}
          setActivePopover={setActivePopover}
          sidebarMenu={sidebarMenu}
          ref={settingRef}
        />
      )}
      <Tooltip
        disable={sidebarMenu}
        content="Expand"
        position="right"
        key="Expand"
      >
        <Button
          variant="secondary"
          data-testid="sidebar-toggle"
          className={twMerge(
            "btn-default !border-none hover:bg-teal-50",
            !isExpanded && "rotate-180",
            sidebarMenu && "w-[231px] justify-start"
          )}
          aria-label="Toggle Sidebar"
          onClick={toggleSidebar}
        >
          <DoubleChevLeftIcon />
          <span
            className={twMerge(
              "whitespace-pre text-sm font-medium",
              !sidebarMenu && "opacity-0 translate-x-28 overflow-hidden hidden"
            )}
          >
            Collapse
          </span>
        </Button>
      </Tooltip>
      <Divider className="bg-white-smoke w-full h-[4px]" />
      <UserMenuWrapper
        activePopover={activePopover}
        isPopoverTriggerOrigin={isPopoverTriggerOrigin}
        setActivePopover={setActivePopover}
        sidebarMenu={sidebarMenu}
        setShowMyProfilePanel={setShowMyProfilePanel}
        setShowChangePasswordPanel={setShowChangePasswordPanel}
        ref={userRef}
      />
      <MyProfilePanel
        showMyProfilePanel={showMyProfilePanel}
        setShowMyProfilePanel={setShowMyProfilePanel}
      />
      <ChangePasswordPanel
        showChangePasswordPanel={showChangePasswordPanel}
        setShowChangePasswordPanel={setShowChangePasswordPanel}
      />
    </div>
  );
};

export default SidebarFooter;
